import React, { useEffect, useState } from "react";
import axios from "axios";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import Constants from "../../utils/Constants";
import SplashScreen from "../../components/loader/SplashScreen/SplashScreen";
import { Export, Restart, View } from "@carbon/icons-react";
import ReactPaginate from "react-paginate";
import SheRunsApplicationDetail from "./SheRunsApplicationDetail";
import {
  PAGE_SIZE,
  STATUS_OPTIONS,
  formatLabel,
  statusColor,
} from "./sheRunsLabels";

const MIN_SEARCH_CHARS = 3;
const QUERY_KEY = "SheRunsApplications";

const SheRunsApplications = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchparam, setSearchparam] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Debounce search: apply at 3+ chars, clear when empty
  useEffect(() => {
    const trimmed = searchInput.trim();
    const timer = setTimeout(() => {
      if (trimmed.length === 0) {
        setSearchparam("");
      } else if (trimmed.length >= MIN_SEARCH_CHARS) {
        setSearchparam(trimmed);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to first page when filters change (skip if already on page 1)
  useEffect(() => {
    setPage((current) => (current === 1 ? current : 1));
  }, [searchparam, startDate, endDate, status]);

  const filters = { searchparam, startDate, endDate, status, page };

  const {
    data: listResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY, filters],
    enabled: !showDetail,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 0,
    queryFn: async ({ queryKey }) => {
      const [, f] = queryKey;
      const res = await axios({
        method: "get",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          page: f.page || 1,
          limit: PAGE_SIZE,
          sort: "-createdAt",
          ...(f.searchparam ? { search: f.searchparam } : {}),
          ...(f.status ? { status: f.status } : {}),
          ...(f.startDate ? { startDate: f.startDate } : {}),
          ...(f.endDate ? { endDate: f.endDate } : {}),
        },
        url: Constants.url + "she-runs-applications",
      });
      return res.data;
    },
  });

  const applications = listResponse?.data?.data || [];
  const totalRecords = listResponse?.totalRecords ?? listResponse?.results ?? 0;
  const showInitialLoader = isLoading && !listResponse;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = () => {
    setExporting(true);
    axios({
      method: "get",
      params: {
        ...(searchparam ? { search: searchparam } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(status ? { status } : {}),
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      url: Constants.url + "she-runs-applications/download",
    })
      .then((res) => {
        if (res.data.status === "success") {
          const downloadUrl = `${Constants.url}image${res.data.filePath}`;
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", "");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .finally(() => setExporting(false));
  };

  const handleViewDetails = (application) => {
    setSelectedId(application._id);
    setShowDetail(true);
  };

  const handleCloseDetail = (shouldRefresh) => {
    setShowDetail(false);
    setSelectedId(null);
    if (shouldRefresh) {
      // Soft invalidate — query re-runs via enabled:true, no stuck isFetching overlay
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    }
  };

  if (showDetail && selectedId) {
    return (
      <SheRunsApplicationDetail
        applicationId={selectedId}
        onBack={handleCloseDetail}
      />
    );
  }

  if (showInitialLoader) {
    return <SplashScreen />;
  }

  return (
    <div className="dashboardWrp">
      <div className="uk-container uk-container-expand">
        <div className="dashboardTop">
          <div className="uk-flex uk-flex-between uk-flex-middle uk-flex-wrap">
            <div>
              <h2 className="uk-margin-remove">SHE RUNS Applications</h2>
              <p className="uk-text-muted uk-margin-remove-top uk-margin-small-bottom">
                Training-camp applications · {totalRecords} total
              </p>
            </div>
          </div>
        </div>

        <div className="paginationcard uk-card uk-card-default uk-padding">
          <div
            className="uk-flex uk-flex-between uk-flex-middle uk-flex-wrap"
            style={{ gap: "16px" }}
          >
            <div
              className="uk-flex uk-flex-wrap uk-flex-middle"
              style={{ gap: "12px" }}
            >
              <div className="formInput" style={{ minWidth: 200 }}>
                <label>Search</label>
                <input
                  className="uk-input"
                  type="text"
                  placeholder="Name, email, phone, CNIC…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput.trim().length > 0 &&
                  searchInput.trim().length < MIN_SEARCH_CHARS && (
                    <small className="uk-text-muted">
                      Type at least {MIN_SEARCH_CHARS} characters
                    </small>
                  )}
              </div>

              <div className="formInput" style={{ minWidth: 150 }}>
                <label>Status</label>
                <select
                  className="uk-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="formInput">
                <label>Start date</label>
                <input
                  type="date"
                  className="uk-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="formInput">
                <label>End date</label>
                <input
                  type="date"
                  className="uk-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="uk-flex uk-flex-middle" style={{ gap: "8px" }}>
              <button
                className="uk-button uk-button-text"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <Restart /> {refreshing ? "Refreshing…" : "Refresh"}
              </button>
              <button
                className="uk-button csvexp"
                onClick={handleExport}
                disabled={exporting}
              >
                <Export size={18} style={{ marginRight: 6 }} />
                {exporting ? "Exporting…" : "Export"}
              </button>
            </div>
          </div>
        </div>

        <div
          className="analyticsTableWrp uk-overflow-auto uk-margin-top"
          style={{
            opacity: isFetching && !showInitialLoader ? 0.75 : 1,
            transition: "opacity 0.15s ease",
          }}
        >
          <table className="uk-table uk-table-striped uk-table-middle">
            <thead className="table-header-shadow">
              <tr>
                <th>S.No</th>
                <th>Full name</th>
                <th>CNIC</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Area</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td colSpan={9} className="uk-text-center uk-text-muted">
                    No applications found
                  </td>
                </tr>
              ) : (
                applications.map((item, index) => (
                  <tr key={item._id}>
                    <td>{(page - 1) * PAGE_SIZE + (index + 1)}</td>
                    <td style={{ fontWeight: 500 }}>{item.fullName || "-"}</td>
                    <td>{item.cnic || "-"}</td>
                    <td>{item.email || "-"}</td>
                    <td>{item.phone || "-"}</td>
                    <td>{item.area || "-"}</td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "16px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor: statusColor(item.status),
                          color: "#fff",
                        }}
                      >
                        {formatLabel(item.status)}
                      </span>
                    </td>
                    <td>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </td>
                    <td>
                      <button
                        className="uk-button uk-button-text"
                        onClick={() => handleViewDetails(item)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "#666",
                          padding: 0,
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        <View size={18} />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="paginationcard uk-card uk-card-default">
            <ReactPaginate
              previousLabel={"←"}
              nextLabel={"→"}
              breakLabel={"..."}
              breakClassName={"break-me"}
              pageCount={Math.max(1, Math.ceil(totalRecords / PAGE_SIZE) || 1)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={(e) => {
                const nextPage = e.selected + 1;
                if (nextPage !== page) setPage(nextPage);
              }}
              containerClassName={"pagination"}
              activeClassName={"active"}
              forcePage={Math.min(
                page - 1,
                Math.max(0, Math.ceil(totalRecords / PAGE_SIZE) - 1)
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SheRunsApplications;
